import React, { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { CommissionChartData, TimePeriod } from '@/types/dashboard.types';

interface CommissionChartProps {
  data: CommissionChartData[];
  isLoading?: boolean;
  onPeriodChange?: (period: TimePeriod) => void;
}

const periodOptions: { value: TimePeriod; label: string }[] = [
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year', label: 'This Year' },
];

/**
 * Format amount in Indian currency format
 */
const formatCurrency = (amount: number, compact = false): string => {
  if (compact) {
    if (amount >= 100000) {
      return `${(amount / 100000).toFixed(1)}L`;
    }
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    }
    return amount.toString();
  }

  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const LoadingSkeleton: React.FC = () => (
  <div className="animate-pulse">
    <div className="flex items-center justify-between mb-6">
      <div className="h-6 bg-secondary-200 rounded w-40"></div>
      <div className="h-8 bg-secondary-100 rounded w-28"></div>
    </div>
    <div className="h-64 bg-secondary-100 rounded-lg"></div>
  </div>
);

export const CommissionChart: React.FC<CommissionChartProps> = ({
  data,
  isLoading = false,
  onPeriodChange,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('month');

  const handlePeriodChange = (period: TimePeriod) => {
    setSelectedPeriod(period);
    onPeriodChange?.(period);
  };

  if (isLoading) {
    return (
      <Card className="h-full">
        <LoadingSkeleton />
      </Card>
    );
  }

  // Calculate max value for scaling
  const maxValue = Math.max(...data.map((d) => d.amount), 1);
  const chartHeight = 180;

  // Calculate total
  const totalCommission = data.reduce((sum, d) => sum + d.amount, 0);
  const avgCommission = data.length > 0 ? totalCommission / data.length : 0;

  return (
    <Card className="h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900">Commission Earned</h3>
          <p className="text-sm text-secondary-500 mt-1">
            Total: {formatCurrency(totalCommission)}
          </p>
        </div>
        <select
          value={selectedPeriod}
          onChange={(e) => handlePeriodChange(e.target.value as TimePeriod)}
          className="text-sm border border-secondary-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {periodOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Average indicator */}
      {avgCommission > 0 && (
        <div className="mb-4 px-3 py-2 bg-green-50 rounded-lg inline-flex items-center gap-2">
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          <span className="text-sm text-green-700">
            Avg: {formatCurrency(avgCommission)} per period
          </span>
        </div>
      )}

      {/* Chart */}
      {data.length === 0 ? (
        <div className="flex items-center justify-center h-48 bg-secondary-50 rounded-lg">
          <div className="text-center">
            <svg className="w-12 h-12 text-secondary-300 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-secondary-500">No commission data available</p>
          </div>
        </div>
      ) : (
        <div className="relative">
          {/* Y-axis labels */}
          <div className="absolute left-0 top-0 flex flex-col justify-between text-xs text-secondary-400" style={{ height: chartHeight }}>
            <span>{formatCurrency(maxValue, true)}</span>
            <span>{formatCurrency(maxValue / 2, true)}</span>
            <span>0</span>
          </div>

          {/* Chart area */}
          <div className="ml-10">
            {/* Line chart with area fill */}
            <svg
              className="w-full overflow-visible"
              style={{ height: chartHeight }}
              viewBox={`0 0 ${data.length * 60} ${chartHeight}`}
              preserveAspectRatio="none"
            >
              {/* Grid lines */}
              <line x1="0" y1="0" x2={data.length * 60} y2="0" stroke="#e5e7eb" strokeWidth="1" />
              <line x1="0" y1={chartHeight / 2} x2={data.length * 60} y2={chartHeight / 2} stroke="#e5e7eb" strokeWidth="1" />
              <line x1="0" y1={chartHeight} x2={data.length * 60} y2={chartHeight} stroke="#e5e7eb" strokeWidth="1" />

              {/* Area fill */}
              <defs>
                <linearGradient id="commissionGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#22c55e" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#22c55e" stopOpacity="0.05" />
                </linearGradient>
              </defs>

              {/* Area path */}
              <path
                d={`
                  M 0 ${chartHeight}
                  ${data.map((d, i) => {
                    const x = i * 60 + 30;
                    const y = chartHeight - (d.amount / maxValue) * chartHeight;
                    return `L ${x} ${y}`;
                  }).join(' ')}
                  L ${data.length * 60} ${chartHeight}
                  Z
                `}
                fill="url(#commissionGradient)"
              />

              {/* Line */}
              <path
                d={`
                  M ${30} ${chartHeight - (data[0]?.amount / maxValue) * chartHeight}
                  ${data.slice(1).map((d, i) => {
                    const x = (i + 1) * 60 + 30;
                    const y = chartHeight - (d.amount / maxValue) * chartHeight;
                    return `L ${x} ${y}`;
                  }).join(' ')}
                `}
                fill="none"
                stroke="#22c55e"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Data points */}
              {data.map((d, i) => {
                const x = i * 60 + 30;
                const y = chartHeight - (d.amount / maxValue) * chartHeight;
                return (
                  <g key={i} className="group cursor-pointer">
                    <circle
                      cx={x}
                      cy={y}
                      r="6"
                      fill="#22c55e"
                      stroke="white"
                      strokeWidth="2"
                      className="transition-all duration-200 hover:r-8"
                    />
                    {/* Tooltip */}
                    <g className="opacity-0 hover:opacity-100 transition-opacity">
                      <rect
                        x={x - 40}
                        y={y - 35}
                        width="80"
                        height="24"
                        rx="4"
                        fill="#1f2937"
                      />
                      <text
                        x={x}
                        y={y - 18}
                        textAnchor="middle"
                        fill="white"
                        fontSize="11"
                      >
                        {formatCurrency(d.amount, true)}
                      </text>
                    </g>
                  </g>
                );
              })}
            </svg>

            {/* X-axis labels */}
            <div className="flex justify-around mt-3">
              {data.map((item, index) => (
                <span
                  key={index}
                  className="text-xs text-secondary-500 text-center"
                  style={{ width: '60px' }}
                >
                  {item.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default CommissionChart;
